
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './Header.css';

interface HeaderProps {
  onSettingsClick: () => void;
  theme: 'light' | 'dark';
  onThemeToggle: () => void;
  showSettingsButton?: boolean;
}

const Header: React.FC<HeaderProps> = ({ onSettingsClick, theme, onThemeToggle, showSettingsButton = true }) => {
  const { t, i18n } = useTranslation();
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const languageMenuRef = useRef<HTMLDivElement>(null);

  const languages = [
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'zh-HK', name: '粵語', flag: '🇭🇰' },
    { code: 'zh-TW', name: '繁體中文', flag: '🇨🇳' },
    { code: 'zh', name: '简体中文', flag: '🇨🇳' },
    { code: 'ja', name: '日本語', flag: '🇯🇵' },
    { code: 'ko', name: '한국어', flag: '🇰🇷' },
  ];

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  const changeLanguage = (langCode: string) => {
    i18n.changeLanguage(langCode);
    setShowLanguageMenu(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (languageMenuRef.current && !languageMenuRef.current.contains(event.target as Node)) {
        setShowLanguageMenu(false);
      }
    };

    if (showLanguageMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showLanguageMenu]);

  return (
    <header className="header" role="banner">
      <div className="header-container">
        <div className="header-logo">
          <h1 className="logo-heading">
            <svg className="logo-icon" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <rect width="100" height="100" rx="22" ry="22" />
              <path d="m35 50 15-20 15 20Z" fill="#fff" />
              <path d="M62 76a16 16 0 0 0 13-28 22 22 0 0 0-50 0 16 16 0 0 0 7 28c10 0 18-5 18-20v-8" fill="none" stroke="#fff" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span className="logo-text">{t('header.logo')}</span>
          </h1>
        </div>
        
        <nav className="header-actions" role="navigation" aria-label="Main navigation">
          <div className="language-selector" ref={languageMenuRef}>
            <button 
              className="language-button"
              onClick={() => setShowLanguageMenu(!showLanguageMenu)}
              aria-label={t('header.language')}
              aria-haspopup="true"
              aria-expanded={showLanguageMenu}
            >
              <span className="flag">{currentLanguage.flag}</span>
              <span className="button-text">{currentLanguage.code.split('-')[0].toUpperCase()}</span>
            </button>
            {showLanguageMenu && (
              <div className="language-menu" role="menu">
                {languages.map(lang => (
                  <button
                    key={lang.code}
                    className={`language-option ${i18n.language === lang.code ? 'active' : ''}`}
                    onClick={() => changeLanguage(lang.code)}
                    role="menuitem"
                  >
                    <span className="flag">{lang.flag}</span>
                    <span>{lang.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          
          <button 
            className="theme-toggle-button"
            onClick={onThemeToggle}
            aria-label={theme === 'dark' ? t('header.lightMode') : t('header.darkMode')}
            title={theme === 'dark' ? t('header.lightMode') : t('header.darkMode')}
          >
            {theme === 'dark' ? (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
              </svg>
            )}
            <span className="button-text">{theme === 'dark' ? t('header.lightMode') : t('header.darkMode')}</span>
          </button>
          
          <a 
            href="https://github.com/kazeochan/tempbin" 
            target="_blank" 
            rel="noopener noreferrer"
            className="github-link"
            aria-label={t('header.github')}
          >
            <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
            </svg>
            <span>kazeochan</span>
          </a>
          
          {showSettingsButton && (
            <button 
              className="settings-button" 
              onClick={onSettingsClick}
              aria-label={t('header.openSettings')}
              aria-haspopup="dialog"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {t('header.settings')}
            </button>
          )}
        </nav>
      </div>
    </header>
  );
};

export default Header;
